import { useState, useMemo, useCallback } from 'react';
import dateUtil from '../../../utils/dateUtil';
import { Employee } from '../../../hooks/useEmployeeData';

export interface WorkArea {
  id: number;
  name: string;
  [key: string]: any;
}

export interface BreakPeriod {
  id?: number;
  start_time: string;
  end_time: string;
  [key: string]: any;
}

export interface TimeTracking {
  id: number;
  employee: number;
  work_area: number;
  work_area__name?: string;
  start_time: string;
  end_time: string;
  break_start?: string | null;
  break_end?: string | null;
  breakperiods?: BreakPeriod[];
  [key: string]: any;
}

export interface TimeTrackingEditFormData {
  id: number | null;
  employee: string;
  work_area: string;
  date: string;
  start_time: string;
  end_time: string;
  break_start: string;
  break_end: string;
}

export type SortKey = 'employee' | 'workArea' | 'day' | 'source' | 'times' | 'break' | 'net' | 'planned' | 'sum';

export const crRefersToThisTT = (tt: TimeTracking, cr?: any): boolean => {
  if (!tt || !cr) return false;
  const candidates = [
    (cr as any).time_tracking,
    (cr as any).time_tracking_id,
    (cr as any).tt_id,
    (cr as any).ttId,
    (cr as any).source_time_tracking,
    (cr as any).source_time_tracking_id,
  ];
  return candidates.some((v: any) => Number(v) === Number((tt as any).id));
};

export function calcBreakMinutesNumber(tt: TimeTracking): number {
  if (!tt.breakperiods || tt.breakperiods.length === 0) return 0;
  let totalBreakMinutes = 0;
  tt.breakperiods.forEach(breakPeriod => {
    if (breakPeriod.start_time && breakPeriod.end_time) {
      const bs = new Date(breakPeriod.start_time);
      const be = new Date(breakPeriod.end_time);
      if (be > bs) {
        totalBreakMinutes += Math.round((be.getTime() - bs.getTime()) / 60000);
      }
    }
  });
  return totalBreakMinutes;
}

export function calcNetHoursNumber(tt: TimeTracking): number {
  if (!tt.start_time || !tt.end_time) return 0;
  const st = new Date(tt.start_time);
  const et = new Date(tt.end_time);
  let ms = et.getTime() - st.getTime();
  if (tt.breakperiods && tt.breakperiods.length > 0) {
    tt.breakperiods.forEach(breakPeriod => {
      if (breakPeriod.start_time && breakPeriod.end_time) {
        const bs = new Date(breakPeriod.start_time);
        const be = new Date(breakPeriod.end_time);
        if (be > bs) ms -= (be.getTime() - bs.getTime());
      }
    });
  }
  if (ms < 0) return 0;
  return Math.round(ms / 36000) / 100;
}

export function normalizeUTC(iso?: string | null): string {
  if (!iso) return '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?$/.test(iso)) {
    return `${iso}Z`;
  }
  return iso;
}

export function toUTCIfNaiveLocal(iso?: string | null): string | undefined {
  if (!iso) return undefined;
  const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  if (hasTz) return iso;
  const dStr = dateUtil.extractDateFromISO(iso);
  const tStr = dateUtil.extractTimeFromISO(iso);
  const utc = dStr && tStr ? dateUtil.createTenantTimeAsUTC(dStr, tStr) : null;
  return utc || iso;
}

export function formatDay(iso: any): string {
  if (!iso) return '';
  const normalized = toUTCIfNaiveLocal(iso) || normalizeUTC(iso);
  const dt = dateUtil.convertUTCToTenantTime(normalized);
  if (!dt) return '';
  const wd = dt.toLocaleDateString('en-GB', { weekday: 'short' });
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  return `${wd}, ${dd}.${mm}.`;
}

export function extractTimeFromISO(iso: any): string {
  if (!iso) return '';
  const normalized = toUTCIfNaiveLocal(iso) || normalizeUTC(iso);
  const berlinDate = dateUtil.convertUTCToTenantTime(normalized);
  if (!berlinDate) return '';
  const hh = String(berlinDate.getHours()).padStart(2, '0');
  const mm = String(berlinDate.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function extractDateFromISO(iso: any): string {
  if (!iso) return '';
  const normalized = toUTCIfNaiveLocal(iso) || normalizeUTC(iso);
  const berlinDate = dateUtil.convertUTCToTenantTime(normalized);
  if (!berlinDate) return '';
  return `${berlinDate.getFullYear()}-${dateUtil.pad2(berlinDate.getMonth() + 1)}-${dateUtil.pad2(berlinDate.getDate())}`;
}

export const formatTime = (iso: any): string => extractTimeFromISO(iso);

export function calcBreak(tt: any): string {
  if (!tt.breakperiods || tt.breakperiods.length === 0) return '';

  let totalBreakMinutes = 0;
  tt.breakperiods.forEach((breakPeriod: any) => {
    if (breakPeriod.start_time && breakPeriod.end_time) {
      const bs = new Date(breakPeriod.start_time);
      const be = new Date(breakPeriod.end_time);
      if (be > bs) {
        const diffMin = Math.round((be.getTime() - bs.getTime()) / 60000);
        totalBreakMinutes += diffMin;
      }
    }
  });

  if (totalBreakMinutes === 0) return '';
  if (totalBreakMinutes < 60) return `${totalBreakMinutes}min`;
  const h = Math.floor(totalBreakMinutes / 60);
  const m = totalBreakMinutes % 60;
  return `${h}h ${m}m`;
}

export function getRequestedWAId(cr?: any): number | undefined {
  if (!cr) return undefined;
  for (const k of ['requested_work_area', 'proposed_work_area']) {
    if (cr[k] !== undefined && cr[k] !== null && cr[k] !== '') return cr[k];
  }
  return undefined;
}

export function useWorkedHoursMap(timeTrackings: TimeTracking[]): Record<number, number> {
  return useMemo(() => {
    const map: Record<number, number> = {};
    timeTrackings.forEach(tt => {
      if (!tt.start_time || !tt.end_time) return;
      const st = new Date(tt.start_time);
      const et = new Date(tt.end_time);
      let ms = et.getTime() - st.getTime();
      if (tt.breakperiods && tt.breakperiods.length > 0) {
        tt.breakperiods.forEach(breakPeriod => {
          if (breakPeriod.start_time && breakPeriod.end_time) {
            const bs = new Date(breakPeriod.start_time);
            const be = new Date(breakPeriod.end_time);
            if (be > bs) {
              ms -= (be.getTime() - bs.getTime());
            }
          }
        });
      }
      const empId = (tt as any).employee;
      if (!empId) return;
      if (!map[empId]) map[empId] = 0;
      map[empId] += Math.max(0, ms / 3600000);
    });
    return map;
  }, [timeTrackings]);
}

export function useSortedTimeTrackings(
  timeTrackings: TimeTracking[],
  sortBy: SortKey,
  sortDir: 'asc' | 'desc',
  employees: Employee[],
  plannedHoursMap: Record<number, number>,
  workedHoursMap: Record<number, number>,
): TimeTracking[] {
  return useMemo(() => {
    const list = [...timeTrackings];
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const aEmp = employees.find(e => e.id === a.employee);
      const bEmp = employees.find(e => e.id === b.employee);
      const aName = aEmp ? `${aEmp.first_name} ${aEmp.last_name}` : '';
      const bName = bEmp ? `${bEmp.first_name} ${bEmp.last_name}` : '';
      const aWA = a.work_area__name || '';
      const bWA = b.work_area__name || '';
      const aDay = a.start_time ? new Date(a.start_time).getTime() : 0;
      const bDay = b.start_time ? new Date(b.start_time).getTime() : 0;
      const aStart = a.start_time ? new Date(a.start_time).getTime() : 0;
      const bStart = b.start_time ? new Date(b.start_time).getTime() : 0;
      const aBreakMin = calcBreakMinutesNumber(a);
      const bBreakMin = calcBreakMinutesNumber(b);
      const aNet = calcNetHoursNumber(a);
      const bNet = calcNetHoursNumber(b);
      const aPlanned = plannedHoursMap[a.employee] || 0;
      const bPlanned = plannedHoursMap[b.employee] || 0;
      const aSum = (workedHoursMap[a.employee] || 0) + aPlanned;
      const bSum = (workedHoursMap[b.employee] || 0) + bPlanned;

      switch (sortBy) {
        case 'employee':
          return aName.localeCompare(bName) * dir;
        case 'workArea':
          return aWA.localeCompare(bWA) * dir;
        case 'day':
          return (aDay - bDay) * dir;
        case 'source': {
          const aSrc = String(((a as any).created_via || (a as any).source_type || (a as any).origin || '')).toLowerCase();
          const bSrc = String(((b as any).created_via || (b as any).source_type || (b as any).origin || '')).toLowerCase();
          return aSrc.localeCompare(bSrc) * dir;
        }
        case 'times':
          return (aStart - bStart) * dir;
        case 'break':
          return (aBreakMin - bBreakMin) * dir;
        case 'net':
          return (aNet - bNet) * dir;
        case 'planned':
          return (aPlanned - bPlanned) * dir;
        case 'sum':
          return (aSum - bSum) * dir;
        default:
          return 0;
      }
    });
    return list;
  }, [timeTrackings, sortBy, sortDir, employees, plannedHoursMap, workedHoursMap]);
}

export function useToggleSort(
  sortBy: SortKey,
  setSortBy: (k: SortKey) => void,
  setSortDir: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>,
): (key: SortKey, isNumeric?: boolean) => void {
  return useCallback((key: SortKey, isNumeric = false): void => {
    if (sortBy === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(isNumeric ? 'desc' : 'asc');
    }
  }, [sortBy, setSortBy, setSortDir]);
}
